import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { FileText } from "lucide-react";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "What is an RFP response generator?", answer: "An RFP response generator is a tool that automatically creates draft answers to RFP questions based on your organisation's knowledge and the specific requirements of each RFP." },
  { question: "Can AI generate a complete RFP response?", answer: "AI can generate structured first drafts for every section. Human review and refinement are essential for quality, accuracy, and strategic positioning." },
  { question: "Is Sorvaine's RFP generator free?", answer: "Sorvaine offers a demo so you can see the generator in action. Book a demo for pricing tailored to your team." },
];

export default function RfpResponseGenerator() {
  return (
    <ContentPageLayout
      title="AI RFP Response Generator | Sorvaine"
      description="Generate complete RFP responses with AI. Sorvaine matches your vault content to RFP questions and drafts proposals automatically."
      canonical={`${BASE}/rfp-response-generator`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "RFP Response Generator", url: `${BASE}/rfp-response-generator` },
      ]}
      ctaHeading="Try the full platform"
      ctaDescription="See how Sorvaine generates complete RFP responses from your uploaded documents."
    >
      <SchemaMarkup type="software" name="Sorvaine RFP Response Generator" description="AI-powered tool that generates structured RFP response drafts from uploaded documents." url={`${BASE}/rfp-response-generator`} category="BusinessApplication" />
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        Free RFP Response Generator
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[620px]">
        Upload an RFP. Get a structured first draft for every section. Sorvaine's AI reads the document, extracts requirements, and writes responses — in minutes, not days.
      </p>

      {/* How it works */}
      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">How it works</h2>
        <div className="mt-6 space-y-6">
          {[
            { step: "1", title: "Upload your RFP", desc: "PDF, Word, or any standard format. Sorvaine parses the full document automatically." },
            { step: "2", title: "AI extracts requirements", desc: "Every question, compliance criterion, and deliverable is identified and structured into sections." },
            { step: "3", title: "First drafts generated", desc: "Section-by-section responses are written based on the RFP context and your organisation's knowledge." },
            { step: "4", title: "Your team refines", desc: "Review, edit, and enhance the drafts. Submit a polished response in a fraction of the usual time." },
          ].map((s) => (
            <div key={s.step} className="flex gap-4 items-start">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary text-sm font-bold shrink-0">
                {s.step}
              </span>
              <div>
                <h3 className="text-sm font-semibold text-white/80">{s.title}</h3>
                <p className="mt-1 text-sm text-white/40">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sample output */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">What you get</h2>
        <div className="mt-6 p-6 rounded-lg border border-white/[0.06] bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-white/30">Sample output</span>
          </div>
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-semibold text-white/70">Section 3.1 — Technical Approach</p>
              <p className="mt-1 text-white/40 leading-relaxed">
                Our approach to the proposed implementation follows a three-phase methodology: discovery, delivery, and optimisation. During discovery, we conduct stakeholder interviews and requirements mapping to ensure alignment with the stated objectives outlined in Section 2.4 of this RFP...
              </p>
            </div>
            <div className="pt-3 border-t border-white/[0.04]">
              <p className="font-semibold text-white/70">Section 4.2 — Team Qualifications</p>
              <p className="mt-1 text-white/40 leading-relaxed">
                The proposed team brings a combined 47 years of experience in enterprise systems integration. Our lead consultant has delivered 12 projects of comparable scope in the healthcare sector, including three for NHS trusts...
              </p>
            </div>
          </div>
          <p className="mt-4 text-[11px] text-white/20 italic">AI-generated first draft — refined by your team before submission</p>
        </div>
      </section>

      {/* Difference from templates */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">More than a template</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Templates give you structure. Sorvaine gives you structure <em>and</em> content. Every generated response is tailored to the specific RFP — it references the buyer's requirements, uses appropriate terminology, and follows your organisation's tone.
        </p>
        <p className="mt-3 text-white/50 leading-relaxed">
          As your team uses Sorvaine, the AI learns from your edits and builds a knowledge base. Each response gets better than the last.
        </p>
      </section>

      {/* FAQ */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">FAQ</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Internal links */}
      <section className="mt-14">
        <h2 className="text-xl font-semibold text-white/80">Learn more</h2>
        <ul className="mt-4 space-y-2">
          <li><Link to="/rfp-response-software" className="text-sm text-primary hover:underline">RFP Response Software — Full Platform Overview →</Link></li>
          <li><Link to="/resources/what-is-rfp-response-software" className="text-sm text-primary hover:underline">What Is RFP Response Software?</Link></li>
          <li><Link to="/resources/rfp-response-template" className="text-sm text-primary hover:underline">RFP Response Template: Free Download</Link></li>
        </ul>
      </section>
    </ContentPageLayout>
  );
}
